import { useState } from 'react';
import { 
  Send, 
  Globe, 
  Phone, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  RefreshCw,
  Upload,
  ExternalLink,
  Download,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useSubmissionLane } from '@/hooks/useSubmissionLane';
import { AuthorizationRequest } from '@/lib/authorization/types';
import { 
  LANE_LABELS, 
  LANE_DESCRIPTIONS, 
  STATUS_COLORS, 
  STATUS_LABELS,
  SubmissionLaneType,
} from '@/lib/submission/types';
import { format } from 'date-fns';
import { PortalSubmissionWorkflow } from './PortalSubmissionWorkflow';
import { FaxPhoneSubmissionWorkflow } from './FaxPhoneSubmissionWorkflow';
import { SubmissionLogList } from './SubmissionLogList';

interface SubmissionLanePanelProps {
  caseData: AuthorizationRequest;
  isLinkOps: boolean;
}

export function SubmissionLanePanel({ caseData, isLinkOps }: SubmissionLanePanelProps) {
  const [activeWorkflow, setActiveWorkflow] = useState<SubmissionLaneType | null>(null);
  
  const {
    submissionLogs,
    config,
    loading,
    submitting,
    submitViaPAS,
    startPortalSubmission,
    startFaxPhoneSubmission,
    confirmSubmission,
    retrySubmission,
    isPASAvailable,
    refetch,
  } = useSubmissionLane({
    authorizationId: caseData.id,
    payerId: caseData.payer_id,
  });

  const latestLog = submissionLogs[0];
  const hasActiveSubmission = latestLog && ['PENDING', 'SUBMITTED', 'RETRYING'].includes(latestLog.status);

  const handleStartPAS = async () => {
    if (!config?.fhir_pas_endpoint) return;
    
    const result = await submitViaPAS(caseData, config.fhir_pas_endpoint);
    if (!result.success) {
      console.error('PAS submission failed:', result.error);
    }
  };

  const handleStartPortal = async () => {
    const logId = await startPortalSubmission();
    if (logId) {
      setActiveWorkflow('PORTAL');
    }
  };

  const handleStartFaxPhone = async () => {
    const logId = await startFaxPhoneSubmission('fax', {
      fax_number: config?.fax_number || caseData.payer?.fax,
      phone_number: config?.phone_number || caseData.payer?.phone,
    });
    if (logId) {
      setActiveWorkflow('FAX_PHONE');
    }
  };

  const handleConfirmSubmission = async (data: {
    reference_id?: string;
    confirmation_number?: string;
    proof_file_path?: string;
    proof_url?: string;
    notes?: string;
  }) => {
    if (!latestLog) return;
    await confirmSubmission(latestLog.id, data);
    setActiveWorkflow(null);
  };

  const handleCancelWorkflow = () => {
    setActiveWorkflow(null);
  };

  if (loading) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            Loading submission data...
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show active workflow
  if (activeWorkflow === 'PORTAL' && latestLog) {
    return (
      <PortalSubmissionWorkflow
        caseData={caseData}
        submissionLog={latestLog}
        config={config}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelWorkflow}
      />
    );
  }

  if (activeWorkflow === 'FAX_PHONE' && latestLog) {
    return (
      <FaxPhoneSubmissionWorkflow
        caseData={caseData}
        submissionLog={latestLog}
        config={config}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelWorkflow}
      />
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" />
            Submission Lanes
          </CardTitle>
          {caseData.active_submission_lane && (
            <Badge variant="outline" className="text-xs">
              Active: {LANE_LABELS[caseData.active_submission_lane as SubmissionLaneType]}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Lane Selection */}
        {isLinkOps && !hasActiveSubmission && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Select a submission method:</p>
            
            <div className="grid gap-2">
              {/* FHIR PAS Lane */}
              <Button
                variant="outline"
                className="justify-start h-auto p-3"
                disabled={!isPASAvailable() || submitting}
                onClick={handleStartPAS}
              >
                <div className="flex items-start gap-3 w-full">
                  <Zap className={`h-5 w-5 mt-0.5 ${isPASAvailable() ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="text-left flex-1">
                    <div className="font-medium">{LANE_LABELS.FHIR_PAS}</div>
                    <div className="text-xs text-muted-foreground">{LANE_DESCRIPTIONS.FHIR_PAS}</div>
                    {!isPASAvailable() && (
                      <div className="text-xs text-amber-500 mt-1">Not configured for this payer</div>
                    )}
                  </div>
                </div>
              </Button>

              {/* Portal Lane */}
              <Button
                variant="outline"
                className="justify-start h-auto p-3"
                disabled={submitting}
                onClick={handleStartPortal}
              >
                <div className="flex items-start gap-3 w-full">
                  <Globe className="h-5 w-5 mt-0.5 text-blue-500" />
                  <div className="text-left flex-1">
                    <div className="font-medium">{LANE_LABELS.PORTAL}</div>
                    <div className="text-xs text-muted-foreground">{LANE_DESCRIPTIONS.PORTAL}</div>
                    {config?.portal_url && (
                      <div className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" />
                        Portal available
                      </div>
                    )}
                  </div>
                </div>
              </Button>

              {/* Fax/Phone Lane */}
              <Button
                variant="outline"
                className="justify-start h-auto p-3"
                disabled={submitting}
                onClick={handleStartFaxPhone}
              >
                <div className="flex items-start gap-3 w-full">
                  <Phone className="h-5 w-5 mt-0.5 text-orange-500" />
                  <div className="text-left flex-1">
                    <div className="font-medium">{LANE_LABELS.FAX_PHONE}</div>
                    <div className="text-xs text-muted-foreground">{LANE_DESCRIPTIONS.FAX_PHONE}</div>
                    {(config?.fax_number || caseData.payer?.fax) && (
                      <div className="text-xs text-orange-500 mt-1">
                        Fax: {config?.fax_number || caseData.payer?.fax}
                      </div>
                    )}
                  </div>
                </div>
              </Button>
            </div>
          </div>
        )}

        {/* Current/Active Submission Status */}
        {hasActiveSubmission && latestLog && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className={STATUS_COLORS[latestLog.status]}>
                  {STATUS_LABELS[latestLog.status]}
                </Badge>
                <span className="text-sm font-medium">
                  {LANE_LABELS[latestLog.lane]}
                </span>
              </div>
              {latestLog.status === 'PENDING' && isLinkOps && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActiveWorkflow(latestLog.lane)}
                >
                  Continue
                </Button>
              )}
            </div>
            
            <div className="text-xs text-muted-foreground">
              Started: {format(new Date(latestLog.executed_at), 'MMM d, yyyy h:mm a')}
            </div>
          </div>
        )}

        {/* Submission Reference (if confirmed) */}
        {caseData.submission_reference_id && (
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">Submission Confirmed</span>
            </div>
            <div className="mt-2 text-sm">
              <span className="text-muted-foreground">Reference: </span>
              <span className="font-mono">{caseData.submission_reference_id}</span>
            </div>
          </div>
        )}

        <Separator />

        {/* Submission Log History */}
        <SubmissionLogList 
          logs={submissionLogs} 
          onRetry={isLinkOps ? retrySubmission : undefined}
        />
      </CardContent>
    </Card>
  );
}
